

import { useState, useEffect, useRef } from 'react';
import type { Note } from '@/app/lib/types';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface StickyNoteCardProps {
  note: Note;
  onUpdate: (id: string, content: string) => void;
  onDelete: (id: string) => void;
}

export function StickyNoteCard({ note, onUpdate, onDelete }: StickyNoteCardProps) {
  const [content, setContent] = useState(note.content);
  const debouncedContent = useDebounce(content, 500);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Only call update if the content has actually changed from the initial prop
    if (debouncedContent !== note.content) {
      onUpdate(note.id, debouncedContent);
    }
  }, [debouncedContent, note.id, note.content, onUpdate]);

  // Adjust textarea height based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [content]);

  return (
    <Card className="bg-yellow-100 dark:bg-yellow-800/20 border-yellow-200 dark:border-yellow-900/50 shadow-md relative group flex flex-col">
      <CardContent className="p-3 flex-grow">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none w-full h-full text-sm leading-relaxed"
          placeholder="Jot down your thoughts..."
        />
      </CardContent>
      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-700/50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this scribble?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your note.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(note.id)}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Card>
  );
}
